import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PasswordStrengthBar } from "@/components/auth/PasswordStrengthBar";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/contexts/ToastContext";
import { supabase } from "@/lib/supabase";
import {
  validateEmail,
  validatePassword,
  validateName,
} from "@/utils/validation";
import { Logger } from "@/utils/logger";
import type { CharityOrganization } from "@/types/charityOrganization";

interface CharityClaimFormProps {
  organization: CharityOrganization;
  onBack: () => void;
}

/** Read-only organization details panel. */
const RegistryDetailsPanel: React.FC<{
  organization: CharityOrganization;
  registryLocation: string;
}> = ({ organization, registryLocation }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
        {t("charity.claim.orgDetails")}
      </h3>
      <p>
        <span className="font-medium text-gray-700">
          {t("charity.claim.orgNameLabel")}:
        </span>{" "}
        {organization.name}
      </p>
      <p>
        <span className="font-medium text-gray-700">
          {t("charity.claim.einLabel")}:
        </span>{" "}
        {organization.ein}
      </p>
      {registryLocation && (
        <p>
          <span className="font-medium text-gray-700">
            {t("charity.claim.locationLabel")}:
          </span>{" "}
          {registryLocation}
        </p>
      )}
    </div>
  );
};

/**
 * Claim registration form for a charity found via IRS search.
 * Displays read-only IRS data and collects contact/password fields.
 * @param props - Component props
 * @param props.organization - The IRS organization to claim
 * @param props.onBack - Called when user clicks back to search
 * @returns The rendered claim form
 */
export const CharityClaimForm: React.FC<CharityClaimFormProps> = ({
  organization,
  onBack,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [formData, setFormData] = useState({
    contactName: "",
    contactEmail: "",
    secondContactName: "",
    secondContactEmail: "",
    password: "",
    confirmPassword: "",
    publicContactEmail: "",
  });
  const [useSignerAsPublicEmail, setUseSignerAsPublicEmail] = useState(true);

  const registryLocation = [
    organization.city,
    organization.state,
    organization.zip,
  ]
    .filter(Boolean)
    .join(", ");

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setError("");

      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const { [name]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [validationErrors],
  );

  const handlePublicEmailToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUseSignerAsPublicEmail(e.target.checked);
    },
    [],
  );

  const validateField = useCallback(
    (name: string, value: string): string => {
      switch (name) {
        case "contactName":
          return validateName(value) ? "" : t("charity.claim.validation.name");
        case "contactEmail":
          return validateEmail(value)
            ? ""
            : t("charity.claim.validation.email");
        case "secondContactName":
          return validateName(value) ? "" : t("charity.claim.validation.name");
        case "secondContactEmail":
          if (!validateEmail(value)) {
            return t("charity.claim.validation.email");
          }
          return value.trim().toLowerCase() ===
            formData.contactEmail.trim().toLowerCase()
            ? t("charity.claim.validation.secondEmailDistinct")
            : "";
        case "password":
          return validatePassword(value)
            ? ""
            : t("charity.claim.validation.password");
        case "confirmPassword":
          return value === formData.password
            ? ""
            : t("charity.claim.validation.confirmPassword");
        default:
          return "";
      }
    },
    [formData.password, formData.contactEmail, t],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      setValidationErrors({});

      const errors: Record<string, string> = {};
      const fieldsToValidate = [
        { name: "contactName", value: formData.contactName },
        { name: "contactEmail", value: formData.contactEmail },
        { name: "secondContactName", value: formData.secondContactName },
        { name: "secondContactEmail", value: formData.secondContactEmail },
        { name: "password", value: formData.password },
        { name: "confirmPassword", value: formData.confirmPassword },
      ];

      fieldsToValidate.forEach(({ name, value }) => {
        const fieldError = validateField(name, value);
        if (fieldError) {
          errors[name] = fieldError;
        }
      });

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setError(t("charity.claim.validation.fix"));
        return;
      }

      setSubmitting(true);

      try {
        // 1. Sign up the user
        const { data: signUpData, error: signUpError } =
          await supabase.auth.signUp({
            email: formData.contactEmail,
            password: formData.password,
            options: {
              data: {
                type: "charity",
                organizationName: organization.name,
                ein: organization.ein,
              },
              emailRedirectTo: `${window.location.origin}/auth/callback?email=${encodeURIComponent(formData.contactEmail)}`,
            },
          });

        if (signUpError) {
          setError(signUpError.message);
          return;
        }

        const userId = signUpData.user?.id;
        if (!userId) {
          setError(t("charity.claim.error.creation"));
          return;
        }

        // 2. Create profiles row
        const { error: profileError } = await supabase
          .from("profiles")
          .insert({ user_id: userId, type: "charity" });

        if (profileError) {
          Logger.error("Failed to create profile row", { error: profileError });
        }

        // 3. Ensure charity_profiles row exists
        const { error: getOrCreateError } = await supabase.rpc(
          "get_or_create_charity_profile",
          {
            lookup_ein: organization.ein,
          },
        );

        if (getOrCreateError) {
          Logger.error("Failed to get/create charity profile", {
            error: getOrCreateError,
          });
        }

        // 4. Claim the charity profile
        const publicEmail = useSignerAsPublicEmail
          ? formData.contactEmail
          : formData.publicContactEmail || null;
        const { error: claimError } = await supabase.rpc(
          "claim_charity_profile",
          {
            p_ein: organization.ein,
            p_signer_name: formData.contactName,
            p_signer_email: formData.contactEmail,
            p_signer_phone: null,
            p_public_contact_email: publicEmail,
            p_second_contact_name: formData.secondContactName,
            p_second_contact_email: formData.secondContactEmail,
          },
        );

        if (claimError) {
          Logger.error("Failed to claim charity profile via RPC", {
            error: claimError,
          });
          // Direct-update fallback: link the profile row when the RPC fails
          // (e.g. due to SQL function parameter issues). Only updates rows
          // where claimed_by is currently NULL to prevent hijacking.
          const { error: fallbackError } = await supabase
            .from("charity_profiles")
            .update({
              claimed_by: userId,
              authorized_signer_name: formData.contactName,
              authorized_signer_email: formData.contactEmail,
              second_contact_name: formData.secondContactName,
              second_contact_email: formData.secondContactEmail,
              status: "claimed-pending",
            })
            .eq("ein", organization.ein)
            .is("claimed_by", null);

          if (fallbackError) {
            Logger.error("Claim fallback direct update also failed", {
              error: fallbackError,
            });
          } else {
            Logger.info("Claim fallback succeeded via direct update", {
              ein: organization.ein,
            });
          }
        }

        showToast({
          type: "success",
          title: t(
            "charity.toast.verificationEmailSent",
            "Verification email sent",
          ),
          message: t(
            "charity.toast.verificationEmailMessage",
            "Check your inbox \u2014 the link expires in 24 hours.",
          ),
        });
        navigate(
          `/auth/registration-success?type=charity-claim&email=${encodeURIComponent(formData.contactEmail)}`,
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t("charity.claim.error.generic");
        showToast({
          type: "error",
          title: t("charity.toast.submissionFailed", "Submission failed"),
          message,
        });
        setError(message);
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData,
      organization,
      validateField,
      navigate,
      t,
      showToast,
      useSignerAsPublicEmail,
    ],
  );

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft aria-hidden="true" className="h-4 w-4 mr-1" />
        {t("charity.claim.backToSearch")}
      </button>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div
            className="p-3 bg-red-50 text-red-600 rounded-md flex items-start"
            role="alert"
          >
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <RegistryDetailsPanel
          organization={organization}
          registryLocation={registryLocation}
        />

        <h3 className="text-lg font-semibold text-gray-900">
          {t("charity.claim.contactInfo")}
        </h3>
        <Input
          label={t("charity.claim.contactName")}
          name="contactName"
          variant="fintech"
          value={formData.contactName}
          onChange={handleChange}
          required
          error={validationErrors["contactName"]}
        />
        <Input
          label={t("charity.claim.contactEmail")}
          type="email"
          name="contactEmail"
          variant="fintech"
          value={formData.contactEmail}
          onChange={handleChange}
          required
          error={validationErrors["contactEmail"]}
        />

        <div className="pt-2">
          <h4 className="text-sm font-medium text-gray-700">
            {t("charity.claim.secondContactTitle", "Second Contact")}
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            {t(
              "charity.claim.secondContactHint",
              "A different person, notified when your charity's wallet or payout details change.",
            )}
          </p>
        </div>
        <Input
          label={t("charity.claim.secondContactName", "Second Contact Name")}
          name="secondContactName"
          variant="fintech"
          value={formData.secondContactName}
          onChange={handleChange}
          required
          error={validationErrors["secondContactName"]}
        />
        <Input
          label={t("charity.claim.secondContactEmail", "Second Contact Email")}
          type="email"
          name="secondContactEmail"
          variant="fintech"
          value={formData.secondContactEmail}
          onChange={handleChange}
          required
          error={validationErrors["secondContactEmail"]}
        />

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={useSignerAsPublicEmail}
              onChange={handlePublicEmailToggle}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-gray-700">
              {t(
                "charity.claim.useAsPublicEmail",
                "Use my email as the charity's public contact email",
              )}
            </span>
          </label>
          {!useSignerAsPublicEmail && (
            <Input
              label={t(
                "charity.claim.publicContactEmail",
                "Organization public contact email",
              )}
              type="email"
              name="publicContactEmail"
              variant="fintech"
              value={formData.publicContactEmail}
              onChange={handleChange}
              error={validationErrors["publicContactEmail"]}
            />
          )}
          <p className="text-xs text-gray-500">
            {t(
              "charity.claim.privacyNotice",
              "Your personal signer email/phone is used for verification only and is not visible to other platform users. The public contact email is shown on your charity profile.",
            )}
          </p>
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {t("charity.claim.accountSecurity")}
        </h3>
        <div className="space-y-1">
          <Input
            label={t("charity.claim.password")}
            type="password"
            name="password"
            variant="fintech"
            value={formData.password}
            onChange={handleChange}
            required
            error={validationErrors["password"]}
          />
          <PasswordStrengthBar password={formData.password} />
        </div>
        <Input
          label={t("charity.claim.confirmPassword")}
          type="password"
          name="confirmPassword"
          variant="fintech"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          error={validationErrors["confirmPassword"]}
        />

        <Button
          type="submit"
          className="w-full bg-gradient-to-b from-emerald-500 to-emerald-600 border border-emerald-700 shadow-none hover:from-emerald-600 hover:to-emerald-700 hover:shadow-none"
          disabled={submitting}
        >
          {submitting ? t("charity.claim.creating") : t("charity.claim.submit")}
        </Button>
      </form>
    </>
  );
};
